import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import { Download as DownloadIcon, Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { createUserRequest, getUserMyRequests, getDownloadUrl, archiveUserRequest, UserRequest } from '../services/userRequestService';

interface PrivacyDialogProps {
  open: boolean;
  onClose: () => void;
}

const PrivacyDialog: React.FC<PrivacyDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [myRequests, setMyRequests] = useState<UserRequest[]>([]);

  const fetchMyRequests = async () => {
      try {
          const data = await getUserMyRequests();
          setMyRequests(data);
      } catch (e) {
          console.error("Failed to fetch requests", e);
      }
  };

  useEffect(() => {
      if (open) {
          fetchMyRequests();
          setMessage(null);
      }
  }, [open]);

  const handleRequest = async (type: 'DELETE_ACCOUNT' | 'EXPORT_DATA') => {
    setLoading(true);
    setMessage(null);
    try {
      await createUserRequest(type);
      setMessage({
        type: 'success',
        text: t('privacy.request_success'),
      });
      fetchMyRequests();
    } catch (err: any) {
        console.error(err);
        const errorMsg = err.response?.data?.message || t('privacy.request_error');
        setMessage({
            type: 'error',
            text: errorMsg
        });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (req: UserRequest) => {
      try {
          const { url } = await getDownloadUrl(req.id);
          // Open in new tab
          window.open(url, '_blank');
          
          // Archive request
          await archiveUserRequest(req.id);
          
          // Refresh list (item should disappear)
          fetchMyRequests();
          
          setMessage({
              type: 'success',
              text: t('privacy.download_started')
          });

      } catch (err) {
          console.error(err);
          setMessage({ type: 'error', text: t('privacy.download_error') });
      }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('privacy.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          {t('privacy.description')}
        </Typography>
        
        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        {/* Active Requests Section */}
        {myRequests.length > 0 && (
            <Box sx={{ mb: 3, mt: 1 }}>
                <Typography variant="subtitle2" gutterBottom>{t('privacy.active_requests')}</Typography>
                <List dense sx={{ bgcolor: 'background.paper', border: '1px solid #eee', borderRadius: 1 }}>
                    {myRequests.map((req) => (
                        <React.Fragment key={req.id}>
                            <ListItem>
                                <ListItemText 
                                    primary={t(`privacy.request_types.${req.requestType ?? 'unknown_request_type'}`)} 
                                    secondary={`${t('status')}: ${req.status}`}
                                />
                                <ListItemSecondaryAction>
                                    {req.status === 'COMPLETED' && req.requestType === 'EXPORT_DATA' ? (
                                        <Button 
                                            variant="contained" 
                                            color="success" 
                                            size="small" 
                                            startIcon={<DownloadIcon />}
                                            onClick={() => handleDownload(req)}
                                        >
                                            {t('userDocuments.download_button')}
                                        </Button>
                                    ) : (
                                        <Typography variant="caption" color="textSecondary">
                                            {req.status === 'PENDING' ? t('privacy.pending_review') : req.status}
                                        </Typography>
                                    )}
                                </ListItemSecondaryAction>
                            </ListItem>
                            <Divider component="li" />
                        </React.Fragment>
                    ))}
                </List>
            </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>{t('privacy.export_data')}</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('privacy.export_description')}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => handleRequest('EXPORT_DATA')}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('privacy.request_export')}
            </Button>
          </Box>

          <Box sx={{ p: 2, border: 1, borderColor: 'error.main', borderRadius: 1 }}>
            <Typography variant="h6" color="error" gutterBottom>{t('privacy.delete_account')}</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('privacy.delete_description')}
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleRequest('DELETE_ACCOUNT')}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('privacy.request_delete')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>{t('close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrivacyDialog;
